/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch, FC, SetStateAction, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Edit3 } from 'lucide-react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { formatColumnName } from '@/utils/formatColumnName';
import { Button } from '../ui/button';
import useInviteClient from '@/hooks/useInviteClient';
import { toast } from 'sonner';
import useUpdateClient from '@/hooks/useUpdateClient';

interface Props {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  client: any;
  columns: string[];
}

const UpdateClientModal: FC<Props> = ({
  client,
  isOpen,
  setIsOpen,
  columns,
}) => {
  const { mutate: updateClient, isPending: updatePending } = useUpdateClient();
  const { mutate: inviteClient, isPending } = useInviteClient();
  const [editingRow, setEditingRow] = useState(client);

  const getEditableFields = () => {
    return columns.filter(
      (col) => col !== 'inviteSent' && col !== 'registeredAt',
    );
  };

  const handleFieldChange = (field: string, value: string) => {
    if (editingRow) {
      setEditingRow((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleInviteUser = (user: any) => {
    inviteClient(
      {
        clientFirstName: user.firstName,
        clientLastName: user.lastName,
        emailAddress: user.emailAddress,
        clientId: user._id,
      },
      {
        onSuccess: () => {
          toast.success('Invite email sent!');
        },
      },
    );
  };

  const handleUpdateClient = () => {
    updateClient(
      {
        id: editingRow._id,
        ...editingRow,
      },
      {
        onSuccess: () => {
          toast.success('Successfully Edited Client');
          setIsOpen(false);
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5" />
            Edit Record
          </DialogTitle>
        </DialogHeader>

        {client && (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {getEditableFields().map((field) => (
                <div key={field} className="space-y-2">
                  <Label htmlFor={field} className="text-sm font-medium">
                    {formatColumnName(field)}
                  </Label>
                  <Input
                    id={field}
                    value={editingRow[field] || ''}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    placeholder={`Enter ${formatColumnName(
                      field,
                    ).toLowerCase()}`}
                    disabled={
                      (field === 'emailAddress' || field === 'emailAddress2') &&
                      !!client.registeredAt
                    }
                  />
                </div>
              ))}
            </div>

            {/* Read-only fields */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-3 text-muted-foreground">
                System Fields (Read-only)
              </h4>
              <div className="flex items-end gap-4">
                <div className="space-y-2 flex-1">
                  <Label className="text-sm font-medium">Invite Sent</Label>
                  <div className="p-2 bg-muted rounded-md text-sm">
                    {client.inviteSent
                      ? new Date(client.inviteSent).toLocaleDateString()
                      : 'Not sent'}
                  </div>
                </div>
                <div className="space-y-2 flex-1">
                  <Label className="text-sm font-medium">Account Created</Label>
                  <div className="p-2 bg-muted rounded-md text-sm">
                    {client.registeredAt
                      ? new Date(client.registeredAt).toLocaleDateString()
                      : 'Not created'}
                  </div>
                </div>
                {!client.registeredAt && (
                  <Button
                    disabled={isPending}
                    onClick={() => handleInviteUser(client)}
                  >
                    Invite
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            disabled={updatePending}
            variant="outline"
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleUpdateClient} disabled={updatePending}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateClientModal;
